import type { ChannelPreviewUIComponentProps } from 'stream-chat-react';
import { ChannelList, useChatContext } from 'stream-chat-react';
import type { Channel, ChannelFilters } from 'stream-chat';
import type { ChannelSort } from 'stream-chat';
import { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { DirectMessagingChannelPreview } from './DirectMessagingChannelPreview';
import { TeamChannelList } from './TeamChannelList';
import { useStreamChatContext } from '~/react_context/StreamChatContext';

const options = { state: true, watch: true, presence: true };
const sort: ChannelSort = { last_message_at: -1 };

type TeamChannelPreviewProps = ChannelPreviewUIComponentProps & {
	type: string;
};

const TeamChannelPreview = ({ name }: { name: string }) => (
	<div title={name}>
		<p className="truncate ...">{`# ${name}`}</p>
	</div>
);

const ChannelPreview = ({ channel, type, unread }: TeamChannelPreviewProps) => {
	const { channel: activeChannel, setActiveChannel } = useChatContext();

	const handleClick = useCallback(() => {
		if (setActiveChannel) {
			setActiveChannel(channel);
		}
	}, [channel, setActiveChannel]);

	const isActive = channel?.id === activeChannel?.id;
	return (
		<button
			className={clsx('text-sm w-full text-left px-2', { 'selected bg-gray-200': isActive, 'font-semibold': !!unread })}
			onClick={handleClick}
		>
			{type === 'team' ? (
				<TeamChannelPreview name={channel?.data?.name || (channel?.data?.id as string) || 'random'} />
			) : (
				<DirectMessagingChannelPreview channel={channel} />
			)}
		</button>
	);
};

const customChannelTeamFilter = (channels: Channel[]) => {
	return channels.filter((channel) => channel.type === 'team');
};

const customChannelMessagingFilter = (channels: Channel[]) => {
	return channels.filter((channel) => channel.type === 'messaging');
};

const EmptyChannelListIndicator = () => <div className="h-20 text-gray-500 text-sm">None</div>;

const TeamChannelsList = () => {
	const { client } = useChatContext();
	const userTeam = client.user?.teams?.[0];
	const channelFilter = useMemo<ChannelFilters>(
		() => ({ members: { $in: [client.userID!] }, type: 'team', team: { $eq: userTeam ?? null } }),
		[client]
	);

	if (!userTeam) {
		return null;
	}

	return (
		<ChannelList
			channelRenderFilterFn={customChannelTeamFilter}
			EmptyStateIndicator={EmptyChannelListIndicator}
			filters={channelFilter}
			List={(listProps) => <TeamChannelList {...listProps} type="team" />}
			options={options}
			Preview={(previewProps) => <ChannelPreview {...previewProps} type="team" />}
			sort={sort}
		/>
	);
};

const OtherTeamChannelsList = () => {
	const { client } = useChatContext();
	const userTeam = client.user?.teams?.[0];
	const channelFilter = useMemo<ChannelFilters>(() => ({ type: 'team', team: { $eq: userTeam ?? null } }), [client]);

	if (!userTeam) {
		return null;
	}

	return (
		<ChannelList
			channelRenderFilterFn={(channels) => {
				// @ts-ignore status appears to have the wrong type
				return channels.filter((channel) => channel.type === 'team' && channel.state.membership.status !== 'member');
			}}
			EmptyStateIndicator={EmptyChannelListIndicator}
			filters={channelFilter}
			List={(listProps) => <TeamChannelList title="Other Channels" {...listProps} type="team" showAddChannelButton={false} />}
			options={options}
			Preview={(previewProps) => <ChannelPreview {...previewProps} type="team" />}
			sort={sort}
		/>
	);
};

const MessagingChannelsList = () => {
	const { client } = useChatContext();
	const messagingFilter = useMemo<ChannelFilters>(() => ({ members: { $in: [client.userID!] }, type: 'messaging' }), [client]);
	return (
		<ChannelList
			channelRenderFilterFn={customChannelMessagingFilter}
			EmptyStateIndicator={EmptyChannelListIndicator}
			filters={messagingFilter}
			List={(listProps) => <TeamChannelList {...listProps} type="messaging" />}
			options={options}
			Preview={(previewProps) => <ChannelPreview {...previewProps} type="messaging" />}
			setActiveChannelOnMount={false}
			sort={sort}
		/>
	);
};

export function ChatSidebar() {
	const { channelListChange } = useStreamChatContext();
	return (
		<div className="min-w-64 max-w-xs px-2 flex flex-col gap-4 overflow-y-auto">
			<TeamChannelsList key={`team-channels-${channelListChange}`} />
			<OtherTeamChannelsList key={`other-team-channels-${channelListChange}`} />
			<MessagingChannelsList key={`messaging-channels-${channelListChange}`} />
		</div>
	);
}
